import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    Send, Mail, User, Upload, FileSpreadsheet, X, Download,
    CheckCircle2, XCircle, AlertTriangle, Info, RefreshCw, UserPlus
} from 'lucide-react';
import {
    sendMocInvite, sendMocInviteExcel, checkMocInviteExcelFormat,
    getMocInvites, mocReinvite, demoExcelDownload
} from '../../redux/ctgSlice';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';

// SEND INVITE — Super Admin, PATIENT-ONLY (route /patient-invite).
//
// The ClickToGo flow (Frontend-web/src/pages/ClickToGo.jsx) is the reference:
// single invite + bulk Excel + pending list with re-invite. Deliberately
// different here:
//   - No doctor tab. A doctor invitation must come from a hospital admin so
//     the doctor joins that hospital (the API refuses it from super_admin) —
//     this page invites PEOPLE to the platform, not staff to a tenant.
//   - The email links to the MOC PATIENT APP's registration page
//     (…/#/register). The invited person creates their own global Mitra One
//     Care account; by the owner's decision no tenant row and no patient row
//     is created by the invite.
//   - Endpoints are the super-admin-gated /api/moc-admin/invites* set (see
//     Backend/api/controllers/mocInviteController.js), not /api/admin/invite.
//
// Same summary contract as ClickToGo ({ successful, failed, duplicate,
// userExists, details }) so the handling below mirrors the reference flow.

// The flat eslint config has no JSX-member-expression tracking, so writing
// motion.div directly in JSX reads as "motion unused" — the alias keeps the
// lint clean.
const MotionDiv = motion.div;

// Validation — identical rules to ClickToGo's patient form.
const EMAIL_REGEX = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const NAME_REGEX = /^[a-zA-Z\s'-]+$/;

const ALLOWED_EXCEL_EXTENSIONS = ['.xlsx', '.xls', '.ods'];
const MAX_EXCEL_SIZE = 5 * 1024 * 1024; // multer limit on the API side

// --- MODULE-LEVEL SUBCOMPONENTS -------------------------------------------
// Never nested inside the page component: an inline component is a new type on
// every render, React remounts its subtree, and inputs lose focus after one
// keystroke. This repo has shipped that bug more than once.

const FieldError = ({ message }) => (
    message
        ? <p className="mt-1 text-xs text-red-500 flex items-center gap-1"><XCircle className="w-3 h-3 flex-shrink-0" />{message}</p>
        : null
);

const SummaryStat = ({ icon, label, value, tone }) => {
    // Local binding (not a destructured param) so no-unused-vars, which cannot
    // see JSX identifier usage here either, ignores it via ^[A-Z_].
    const Icon = icon;
    const tones = {
        good: 'from-emerald-50 to-teal-50 text-emerald-700 border-emerald-200',
        bad: 'from-red-50 to-rose-50 text-red-700 border-red-200',
        warn: 'from-amber-50 to-yellow-50 text-amber-700 border-amber-200',
        info: 'from-blue-50 to-cyan-50 text-sky-700 border-sky-200',
    };
    return (
        <div className={`flex items-center gap-2.5 rounded-xl border bg-gradient-to-br px-3 py-2.5 ${tones[tone] || tones.info}`}>
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span className="text-xs font-semibold">{label}</span>
            <span className="ml-auto text-sm font-bold">{value}</span>
        </div>
    );
};

// Bulk-upload modal — file pick, immediate server-side format check, upload.
const ExcelUploadModal = ({
    t, isOpen, onClose, file, fileError, formatChecking, formatValid,
    onFileSelect, onClearFile, onUpload, isUploading, onDownloadSample, isDownloading
}) => {
    const inputRef = useRef(null);
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <MotionDiv
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-5 py-4">
                    <h3 className="flex items-center gap-2 text-white font-semibold text-sm">
                        <FileSpreadsheet className="w-4 h-4" />
                        {t('clicktogo.excel_upload_patient', 'Upload Excel File - Patient Invites')}
                    </h3>
                    <button onClick={onClose} aria-label={t('clicktogo.close_modal', 'Close modal')} className="text-white/80 hover:text-white cursor-pointer transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    <p className="text-xs text-gray-500 leading-relaxed">
                        {t('clicktogo.moc_excel_hint', 'The spreadsheet needs an "Email" column; a "Name" column is optional. Every listed address receives the Mitra One Care patient app registration link.')}
                    </p>

                    <input
                        ref={inputRef}
                        type="file"
                        accept=".xlsx,.xls,.ods"
                        className="hidden"
                        onChange={(e) => {
                            const picked = e.target.files?.[0];
                            if (picked) onFileSelect(picked);
                            e.target.value = '';
                        }}
                    />

                    {!file ? (
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            className="w-full rounded-xl border-2 border-dashed border-[#4ABDBD]/40 bg-gradient-to-br from-blue-50/50 to-cyan-50/50 px-4 py-8 text-center hover:border-[#4ABDBD] transition-colors cursor-pointer"
                        >
                            <Upload className="mx-auto mb-2 h-6 w-6 text-[#4ABDBD]" />
                            <span className="block text-sm font-medium text-gray-700">{t('clicktogo.choose_file', 'Choose File')}</span>
                            <span className="mt-1 block text-[11px] text-gray-400">.xlsx, .xls, .ods — {t('clicktogo.moc_max_size', 'max 5MB')}</span>
                        </button>
                    ) : (
                        <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3">
                            <FileSpreadsheet className="h-5 w-5 flex-shrink-0 text-[#4ABDBD]" />
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium text-gray-800">{file.name}</p>
                                <p className="text-[11px] text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                            </div>
                            {formatChecking ? (
                                <RefreshCw className="h-4 w-4 animate-spin text-[#4ABDBD]" />
                            ) : formatValid ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            ) : (
                                <XCircle className="h-4 w-4 text-red-500" />
                            )}
                            <button onClick={onClearFile} aria-label={t('clicktogo.close_modal', 'Close modal')} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    )}

                    {fileError && (
                        <div className="flex items-start gap-2 rounded-xl border-l-4 border-red-400 bg-red-50 p-3">
                            <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                            <p className="text-xs leading-relaxed text-red-700">{fileError}</p>
                        </div>
                    )}
                    {file && formatValid && !formatChecking && (
                        <div className="flex items-start gap-2 rounded-xl border-l-4 border-emerald-400 bg-emerald-50 p-3">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" />
                            <p className="text-xs leading-relaxed text-emerald-700">{t('clicktogo.moc_format_ok', 'File format looks good.')}</p>
                        </div>
                    )}

                    <div className="flex items-center justify-between gap-2 pt-1">
                        <button
                            type="button"
                            onClick={onDownloadSample}
                            disabled={isDownloading}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3AA8A8] hover:text-[#2D8A8A] disabled:opacity-50 cursor-pointer transition-colors"
                        >
                            <Download className="h-3.5 w-3.5" />
                            {isDownloading ? t('clicktogo.downloading', 'Downloading...') : t('clicktogo.download_sample', 'Sample Excel')}
                        </button>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                                {t('clicktogo.cancel', 'Cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={onUpload}
                                disabled={!file || !formatValid || formatChecking || isUploading}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-xs font-semibold text-white shadow-md hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer transition-all duration-300"
                            >
                                {isUploading ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                                {isUploading ? t('clicktogo.uploading', 'Uploading...') : t('clicktogo.upload', 'Upload')}
                            </button>
                        </div>
                    </div>
                </div>
            </MotionDiv>
        </div>
    );
};

// Post-upload result modal — the summary counts plus the two actionable lists
// (duplicates can be re-invited in one click; failures show their errors).
const UploadResultModal = ({ t, result, onClose, onReinviteDuplicates, isReinviting }) => {
    if (!result) return null;
    const { summary } = result;
    const failedRows = (summary?.details || []).filter((d) => d.status === 'failed');
    const duplicateRows = (summary?.details || []).filter((d) => d.status === 'Invitation already exists');
    const existsRows = (summary?.details || []).filter((d) => d.status === 'User already exists');
    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <MotionDiv
                initial={{ opacity: 0, scale: 0.96, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.18 }}
                className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-5 py-4">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
                        <FileSpreadsheet className="w-4 h-4" />
                        {t('clicktogo.upload_results', 'Upload Results')}
                    </h3>
                    <button onClick={onClose} aria-label={t('clicktogo.close_modal', 'Close modal')} className="text-white/80 hover:text-white cursor-pointer transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="space-y-4 overflow-y-auto p-5">
                    <div className="grid grid-cols-2 gap-2.5">
                        <SummaryStat icon={CheckCircle2} tone="good" label={t('clicktogo.successful', 'Successful')} value={summary?.successful ?? 0} />
                        <SummaryStat icon={XCircle} tone="bad" label={t('clicktogo.failed', 'Failed')} value={summary?.failed ?? 0} />
                        <SummaryStat icon={AlertTriangle} tone="warn" label={t('clicktogo.duplicates', 'Duplicates')} value={summary?.duplicate ?? 0} />
                        <SummaryStat icon={Info} tone="info" label={t('clicktogo.moc_already_registered', 'Already registered')} value={summary?.userExists ?? 0} />
                    </div>

                    {duplicateRows.length > 0 && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                            <p className="mb-2 text-xs font-semibold text-amber-800">
                                {t('clicktogo.duplicate_invitations', 'Duplicate Invitations')} — {t('clicktogo.email_already_invited', 'Email already Invited. You can reinvite this user.')}
                            </p>
                            <ul className="mb-3 max-h-28 space-y-1 overflow-y-auto">
                                {duplicateRows.map((d) => (
                                    <li key={d.email} className="truncate text-xs text-amber-700">{d.name ? `${d.name} — ` : ''}{d.email}</li>
                                ))}
                            </ul>
                            <button
                                type="button"
                                onClick={() => onReinviteDuplicates(duplicateRows)}
                                disabled={isReinviting}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:shadow-md disabled:opacity-50 cursor-pointer transition-all duration-200"
                            >
                                {isReinviting ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                {isReinviting ? t('clicktogo.sending_reinvite', 'Sending...') : t('clicktogo.moc_reinvite_all', 'Re-invite all duplicates')}
                            </button>
                        </div>
                    )}

                    {existsRows.length > 0 && (
                        <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3">
                            <p className="mb-1.5 text-xs font-semibold text-sky-800">
                                {t('clicktogo.moc_already_registered_list', 'These emails already have a Mitra One Care account — nothing to invite:')}
                            </p>
                            <ul className="max-h-24 space-y-1 overflow-y-auto">
                                {existsRows.map((d) => (
                                    <li key={d.email} className="truncate text-xs text-sky-700">{d.email}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {failedRows.length > 0 && (
                        <div className="rounded-xl border border-red-200 bg-red-50/60 p-3">
                            <p className="mb-1.5 text-xs font-semibold text-red-800">{t('clicktogo.failed_invitations', 'Failed Invitations')}</p>
                            <ul className="max-h-24 space-y-1 overflow-y-auto">
                                {failedRows.map((d) => (
                                    <li key={d.email} className="truncate text-xs text-red-700">{d.email}{d.error ? ` — ${d.error}` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-100 px-5 py-3 text-right">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-5 py-2 text-xs font-semibold text-white shadow-md hover:shadow-lg cursor-pointer transition-all duration-300"
                    >
                        {t('clicktogo.moc_close', 'Close')}
                    </button>
                </div>
            </MotionDiv>
        </div>
    );
};

function PatientInvite() {
    const dispatch = useDispatch();
    const { t } = useTranslation(['clicktogo', 'patientpage']);
    const {
        isLoading, isUploading, isLoadingInvites, isReinviting, isDownloadingExcel,
        invitations, total
    } = useSelector((state) => state.ctg);

    const [form, setForm] = useState({ username: '', email: '' });
    const [errors, setErrors] = useState({ username: '', email: '' });

    const [isExcelModalOpen, setIsExcelModalOpen] = useState(false);
    const [excelFile, setExcelFile] = useState(null);
    const [excelFileError, setExcelFileError] = useState('');
    const [formatChecking, setFormatChecking] = useState(false);
    const [formatValid, setFormatValid] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [refreshKey, setRefreshKey] = useState(0);
    const [reinvitingEmail, setReinvitingEmail] = useState('');

    useEffect(() => {
        dispatch(getMocInvites({ page: currentPage, limit: itemsPerPage }));
    }, [dispatch, currentPage, itemsPerPage, refreshKey]);

    // Refetch the current page (row re-invite keeps the operator where they
    // are); a new invite jumps to page 1 where the newest row sorts. Both
    // state writes batch into ONE render, so the effect fires once.
    const refreshInvites = useCallback(() => setRefreshKey((k) => k + 1), []);
    const refreshToFirstPage = useCallback(() => {
        setCurrentPage(1);
        setRefreshKey((k) => k + 1);
    }, []);

    // --- Single invite ----------------------------------------------------
    const validateForm = () => {
        const next = { username: '', email: '' };
        const username = form.username.trim();
        const email = form.email.trim();

        if (!username) next.username = t('clicktogo.username_required', 'Username is required');
        else if (username.length < 2) next.username = t('clicktogo.username_min_length', 'Username must be at least 2 characters long');
        else if (username.length > 20) next.username = t('clicktogo.username_max_length', 'Username cannot exceed 20 characters');
        else if (!NAME_REGEX.test(username)) next.username = t('clicktogo.username_invalid_chars', 'Username can only contain letters, spaces, hyphens, and apostrophes');

        if (!email) next.email = t('clicktogo.email_required', 'Email is required');
        else if (email.length > 254) next.email = t('clicktogo.email_too_long', 'Email address is too long (maximum 254 characters)');
        else if (!EMAIL_REGEX.test(email)) next.email = t('clicktogo.invalid_email_format', 'Invalid email format');

        setErrors(next);
        return !next.username && !next.email;
    };

    const confirmReinvite = async (name, email) => {
        const { isConfirmed } = await Swal.fire({
            title: t('clicktogo.re_invite_confirmation_title', 'Re-Invite Confirmation'),
            text: t('clicktogo.re_invite_confirmation_text', 'The email {{email}} already has an invitation. Do you want to reinvite {{name}}?', { email, name: name || email }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#9DB3B1',
            confirmButtonText: t('clicktogo.yes_re_invite', 'Yes, Re-Invite'),
            cancelButtonText: t('clicktogo.cancel', 'Cancel'),
        });
        if (!isConfirmed) return false;
        const result = await dispatch(mocReinvite({ emailSend: [{ name: name || 'User', email }] }));
        if (mocReinvite.fulfilled.match(result) && result.payload?.summary?.successful > 0) {
            toast.success(t('clicktogo.reinvitation_sent_text', 'Reinvitation sent successfully'));
            return true;
        }
        toast.warn(result.payload?.error?.message || t('clicktogo.failed_to_send', 'Failed to send invitation'));
        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        const username = form.username.trim();
        const email = form.email.trim();

        const { isConfirmed } = await Swal.fire({
            title: t('clicktogo.send_invitation_title', 'Send Invitation?'),
            text: t('clicktogo.send_invitation_text', 'Are you sure you want to send an invitation to {{username}} ({{email}})?', { username, email }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#9DB3B1',
            confirmButtonText: t('clicktogo.yes_send_invite', 'Yes, Send Invite'),
            cancelButtonText: t('clicktogo.cancel', 'Cancel'),
        });
        if (!isConfirmed) return;

        const result = await dispatch(sendMocInvite({ emailSend: [{ name: username, email }] }));
        if (!sendMocInvite.fulfilled.match(result)) {
            Swal.fire({
                title: t('clicktogo.error', 'Error'),
                text: result.payload?.error?.message || t('clicktogo.failed_to_send', 'Failed to send invitation'),
                icon: 'error',
                confirmButtonColor: '#4ABDBD',
            });
            return;
        }

        const summary = result.payload?.summary;
        if (summary?.successful > 0) {
            Swal.fire({
                title: t('clicktogo.invitation_sent', 'Invitation Sent!'),
                text: t('clicktogo.invitation_sent_text', 'Invitation sent successfully to {{username}}', { username }),
                icon: 'success',
                confirmButtonColor: '#4ABDBD',
            });
            setForm({ username: '', email: '' });
            setErrors({ username: '', email: '' });
            refreshToFirstPage();
        } else if (summary?.userExists > 0) {
            Swal.fire({
                title: t('clicktogo.moc_already_registered_title', 'Already registered'),
                text: t('clicktogo.moc_already_registered_text', 'This email already has a Mitra One Care account — there is nothing to invite them to.'),
                icon: 'info',
                confirmButtonColor: '#4ABDBD',
            });
        } else if (summary?.duplicate > 0) {
            const reinvited = await confirmReinvite(username, email);
            if (reinvited) {
                setForm({ username: '', email: '' });
                refreshInvites();
            }
        } else {
            const failure = summary?.details?.find((d) => d.status === 'failed');
            Swal.fire({
                title: t('clicktogo.error', 'Error'),
                text: failure?.error || t('clicktogo.failed_to_send', 'Failed to send invitation'),
                icon: 'error',
                confirmButtonColor: '#4ABDBD',
            });
        }
    };

    // --- Bulk Excel -------------------------------------------------------
    const handleFileSelect = async (file) => {
        const ext = `.${file.name.split('.').pop()?.toLowerCase()}`;
        if (!ALLOWED_EXCEL_EXTENSIONS.includes(ext)) {
            setExcelFile(null);
            setFormatValid(false);
            setExcelFileError(t('clicktogo.invalid_file_type', 'Please upload a valid Excel file (.xlsx, .xls, or .csv)'));
            return;
        }
        if (file.size > MAX_EXCEL_SIZE) {
            setExcelFile(null);
            setFormatValid(false);
            setExcelFileError(t('clicktogo.moc_file_size_error', 'File size must be less than 5MB'));
            return;
        }
        setExcelFile(file);
        setExcelFileError('');
        setFormatValid(false);
        setFormatChecking(true);
        const result = await dispatch(checkMocInviteExcelFormat(file));
        setFormatChecking(false);
        if (checkMocInviteExcelFormat.fulfilled.match(result) && result.payload?.valid) {
            setFormatValid(true);
        } else {
            setFormatValid(false);
            const payload = result.payload || {};
            setExcelFileError(
                (Array.isArray(payload.errors) && payload.errors[0]) ||
                payload.error ||
                t('clicktogo.moc_invalid_format', 'This file does not match the expected format.')
            );
        }
    };

    const clearExcelFile = () => {
        setExcelFile(null);
        setExcelFileError('');
        setFormatValid(false);
    };

    const closeExcelModal = () => {
        setIsExcelModalOpen(false);
        clearExcelFile();
    };

    const handleExcelUpload = async () => {
        if (!excelFile || !formatValid) return;
        const { isConfirmed } = await Swal.fire({
            title: t('clicktogo.upload_excel_title', 'Upload Excel File?'),
            text: t('clicktogo.moc_upload_excel_text', 'Send a Mitra One Care patient app invitation to every email in "{{filename}}"?', { filename: excelFile.name }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#9DB3B1',
            confirmButtonText: t('clicktogo.yes_upload', 'Yes, Upload'),
            cancelButtonText: t('clicktogo.cancel', 'Cancel'),
        });
        if (!isConfirmed) return;

        const result = await dispatch(sendMocInviteExcel({ file: excelFile }));
        if (sendMocInviteExcel.fulfilled.match(result)) {
            closeExcelModal();
            setUploadResult(result.payload);
            refreshToFirstPage();
        } else {
            toast.warn(result.payload?.error?.message || t('clicktogo.failed_to_upload', 'Failed to upload Excel file'));
        }
    };

    const handleReinviteDuplicates = async (duplicateRows) => {
        const emailSend = duplicateRows.map((d) => ({ name: d.name || 'User', email: d.email }));
        const result = await dispatch(mocReinvite({ emailSend }));
        if (mocReinvite.fulfilled.match(result)) {
            toast.success(t('clicktogo.reinvitation_sent_text', 'Reinvitation sent successfully'));
            setUploadResult(null);
            refreshInvites();
        } else {
            toast.warn(result.payload?.error?.message || t('clicktogo.failed_to_send', 'Failed to send invitation'));
        }
    };

    const handleDownloadSample = async () => {
        const result = await dispatch(demoExcelDownload());
        if (demoExcelDownload.fulfilled.match(result)) {
            const url = window.URL.createObjectURL(new Blob([result.payload]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'sample-excel.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('clicktogo.sample_excel_downloaded', 'Sample Excel file downloaded successfully'));
        } else {
            toast.warn(t('clicktogo.failed_to_download', 'Failed to download sample Excel file'));
        }
    };

    // --- Table re-invite --------------------------------------------------
    const handleRowReinvite = async (invitation) => {
        setReinvitingEmail(invitation.email);
        try {
            const done = await confirmReinvite(invitation.name, invitation.email);
            if (done) refreshInvites();
        } finally {
            setReinvitingEmail('');
        }
    };

    const handlePageChange = (page, newLimit) => {
        if (newLimit && newLimit !== itemsPerPage) {
            setItemsPerPage(newLimit);
            setCurrentPage(1);
        } else {
            setCurrentPage(page);
        }
    };

    const rows = invitations || [];
    const indexOfFirst = (currentPage - 1) * itemsPerPage;

    return (
        <div data-testid="patient-invite-page" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* Page header — icon tile + h1 + subtitle, the app's standard treatment. */}
            <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] rounded-xl shadow-sm flex-shrink-0">
                    <Send className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                    <h1 className="text-lg sm:text-xl font-bold text-gray-800 truncate">
                        {t('clicktogo.moc_page_title', 'Send Invite')}
                    </h1>
                    <p className="text-xs sm:text-sm text-gray-500">
                        {t('clicktogo.moc_page_subtitle', 'Invite people to the Mitra One Care patient app — the email links to its registration page, where they create their own global account.')}
                    </p>
                </div>
            </div>

            {/* What an invite does (and does not do). */}
            <div className="mb-6 flex items-start gap-2.5 rounded-xl border-l-4 border-[#4ABDBD] bg-gradient-to-r from-blue-50/80 to-cyan-50/80 p-3.5">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#3AA8A8]" />
                <p className="text-xs leading-relaxed text-gray-600">
                    {t('clicktogo.moc_page_note', 'Invites are patient-only. Registering creates one global Mitra One Care account that works across every connected hospital — the invite itself creates no hospital record.')}
                </p>
            </div>

            {/* Single invite card */}
            <div className="mb-8 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-blue-50/30 p-4 sm:p-5 shadow-sm">
                <h2 className="mb-4 flex items-center gap-2 text-sm font-bold text-gray-700">
                    <UserPlus className="h-4 w-4 text-[#3AA8A8]" />
                    {t('clicktogo.moc_single_invite', 'Invite a patient')}
                </h2>
                <form onSubmit={handleSubmit} noValidate>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                        <div className="flex-1">
                            <label htmlFor="invite-username" className="mb-1 block text-xs font-semibold text-gray-600">
                                {t('clicktogo.username_label', 'User Name')}
                            </label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4ABDBD]" />
                                <input
                                    id="invite-username"
                                    data-testid="patient-invite-name"
                                    type="text"
                                    value={form.username}
                                    maxLength={20}
                                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                                    placeholder={t('clicktogo.username_placeholder', 'Enter username')}
                                    className="w-full rounded-xl border border-gray-300 bg-white p-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                />
                            </div>
                            <FieldError message={errors.username} />
                        </div>
                        <div className="flex-1">
                            <label htmlFor="invite-email" className="mb-1 block text-xs font-semibold text-gray-600">
                                {t('clicktogo.email_label', 'Email')}
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#4ABDBD]" />
                                <input
                                    id="invite-email"
                                    data-testid="patient-invite-email"
                                    type="email"
                                    value={form.email}
                                    maxLength={254}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder={t('clicktogo.email_placeholder', 'Enter email')}
                                    className="w-full rounded-xl border border-gray-300 bg-white p-2 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                />
                            </div>
                            <FieldError message={errors.email} />
                        </div>
                        <div className="flex gap-2 sm:pt-[22px]">
                            <button
                                type="submit"
                                data-testid="patient-invite-send"
                                disabled={isLoading}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-4 py-2 text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:scale-105 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100 cursor-pointer transition-all duration-300"
                            >
                                {isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                {isLoading ? t('clicktogo.sending', 'Sending...') : t('clicktogo.send_button', 'Send')}
                            </button>
                            <button
                                type="button"
                                data-testid="patient-invite-bulk"
                                onClick={() => setIsExcelModalOpen(true)}
                                className="inline-flex items-center gap-1.5 rounded-xl border border-[#4ABDBD]/40 bg-white px-4 py-2 text-sm font-semibold text-[#3AA8A8] shadow-sm hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 cursor-pointer transition-all duration-300"
                            >
                                <FileSpreadsheet className="h-4 w-4" />
                                {t('clicktogo.upload_excel', 'Upload Excel')}
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Pending invitations — emails that registered a MOC account are
                excluded server-side, so every row here can still be re-sent. */}
            <h2 className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-700">
                <Mail className="h-4 w-4 text-[#3AA8A8]" />
                {t('clicktogo.moc_pending_invites', 'Pending invitations')}
            </h2>

            {isLoadingInvites ? (
                <div data-testid="patient-invite-loading" className="flex flex-col items-center justify-center py-4 pt-8">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('clicktogo.loading', 'Loading...')}</span>
                </div>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                        <table data-testid="patient-invite-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.s_no', 'S.No')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.name', 'Name')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.email', 'Email')}</th>
                                    <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('clicktogo.invited_by', 'Invited By')}</th>
                                    <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('clicktogo.action', 'Action')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length > 0 ? rows.map((invitation, index) => (
                                    <tr
                                        key={invitation._id || invitation.id || invitation.email}
                                        className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300"
                                    >
                                        <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                        <td className="p-3 text-xs font-medium text-gray-800">{invitation.name || '-'}</td>
                                        <td className="p-3 text-xs text-gray-600">{invitation.email}</td>
                                        <td className="p-3 text-xs text-gray-600">{invitation.adminName || '-'}</td>
                                        <td className="p-3 text-xs">
                                            <div className="flex justify-center">
                                                <button
                                                    data-testid={`patient-invite-resend-${invitation.email}`}
                                                    onClick={() => handleRowReinvite(invitation)}
                                                    disabled={isReinviting}
                                                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 px-3 py-1.5 text-xs font-semibold text-[#3AA8A8] shadow-md hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer transition-all duration-300"
                                                >
                                                    {isReinviting && reinvitingEmail === invitation.email
                                                        ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                                        : <Send className="h-3.5 w-3.5" />}
                                                    {isReinviting && reinvitingEmail === invitation.email
                                                        ? t('clicktogo.sending_reinvite', 'Sending...')
                                                        : t('clicktogo.re_invite', 'Re-Invite')}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr data-testid="patient-invite-empty">
                                        <td colSpan="5" className="p-3 text-center text-gray-500">
                                            {t('clicktogo.moc_no_invites', 'No pending invitations yet — everyone you invited has either registered or is still to be invited.')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile cards */}
                    <div data-testid="patient-invite-mobile-cards" className="block sm:hidden space-y-4">
                        {rows.length > 0 ? rows.map((invitation, index) => (
                            <div key={invitation._id || invitation.id || invitation.email} className="p-4 bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="text-xs font-bold text-[#4ABDBD]">
                                        {t('clicktogo.s_no', 'S.No')}: {indexOfFirst + index + 1}
                                    </span>
                                    <button
                                        onClick={() => handleRowReinvite(invitation)}
                                        disabled={isReinviting}
                                        className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 px-3 py-1.5 text-xs font-semibold text-[#3AA8A8] shadow-md hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white disabled:opacity-50 cursor-pointer transition-all duration-300"
                                    >
                                        {isReinviting && reinvitingEmail === invitation.email
                                            ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                            : <Send className="h-3.5 w-3.5" />}
                                        {t('clicktogo.re_invite', 'Re-Invite')}
                                    </button>
                                </div>
                                <div className="space-y-1.5">
                                    <div className="text-sm font-semibold text-gray-800">{invitation.name || '-'}</div>
                                    <div className="text-xs text-gray-600">
                                        <span className="font-bold">{t('clicktogo.email', 'Email')}:</span> {invitation.email}
                                    </div>
                                    <div className="text-xs text-gray-600">
                                        <span className="font-bold">{t('clicktogo.invited_by', 'Invited By')}:</span> {invitation.adminName || '-'}
                                    </div>
                                </div>
                            </div>
                        )) : (
                            <div className="p-3 text-center text-gray-500">
                                {t('clicktogo.moc_no_invites', 'No pending invitations yet — everyone you invited has either registered or is still to be invited.')}
                            </div>
                        )}
                    </div>
                </>
            )}

            {!isLoadingInvites && total > 0 && (
                <Pagination
                    totalItems={total}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    custombutton={rows.length === 0 ? 'disable' : ''}
                />
            )}

            <AnimatePresence>
                {isExcelModalOpen && (
                    <ExcelUploadModal
                        t={t}
                        isOpen={isExcelModalOpen}
                        onClose={closeExcelModal}
                        file={excelFile}
                        fileError={excelFileError}
                        formatChecking={formatChecking}
                        formatValid={formatValid}
                        onFileSelect={handleFileSelect}
                        onClearFile={clearExcelFile}
                        onUpload={handleExcelUpload}
                        isUploading={isUploading}
                        onDownloadSample={handleDownloadSample}
                        isDownloading={isDownloadingExcel}
                    />
                )}
                {uploadResult && (
                    <UploadResultModal
                        t={t}
                        result={uploadResult}
                        onClose={() => setUploadResult(null)}
                        onReinviteDuplicates={handleReinviteDuplicates}
                        isReinviting={isReinviting}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

export default PatientInvite;
